import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Sparkles, ShieldCheck, Users, Image as ImageIcon, Check } from "lucide-react";
import { api, authedApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../hooks/use-toast";

const UserProfile = () => {
  const { name } = useParams();
  const { user, userToken } = useAuth();
  const { toast } = useToast();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [myBgs, setMyBgs] = useState(null);
  const isOwn = useMemo(() => user?.habbo_name === name, [user, name]);
  const a = useMemo(() => (userToken ? authedApi(userToken) : null), [userToken]);

  useEffect(() => {
    (async () => {
      try { setP(await api.userProfile(name)); }
      catch (err) { setError(err?.response?.data?.detail || "Profil nicht gefunden"); }
      finally { setLoading(false); }
    })();
  }, [name]);

  useEffect(() => {
    if (!isOwn || !a) return;
    (async () => { try { setMyBgs(await a.myBackgrounds()); } catch { /* ignore */ } })();
  }, [isOwn, a]);

  const onActivateBg = async (slug) => {
    try {
      await a.setActiveBackground(slug);
      setP((prev) => ({ ...prev, active_background_slug: slug, active_background_url: slug ? (myBgs?.unlocked.find((b) => b.slug === slug)?.image_url) : null }));
      setMyBgs((prev) => prev ? { ...prev, active: slug } : prev);
      toast({ title: slug ? "Hintergrund aktiv" : "Hintergrund entfernt" });
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (error) return <div className="mx-auto max-w-md px-4 py-20 text-center text-sm text-rose-300">{error}</div>;
  if (!p) return null;

  const glow = p.custom_glow_color;
  const bgUrl = p.active_background_url;

  return (
    <section className="mx-auto max-w-4xl px-4 py-10" data-testid="user-profile-page">
      {/* Profil-Card mit zentriertem Avatar auf Hintergrund */}
      <div
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0f141b]"
        style={bgUrl ? { backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        data-testid="profile-hero-card"
      >
        {/* Dunkler Verlauf damit Text immer lesbar bleibt */}
        <div className="absolute inset-0" style={{
          background: bgUrl
            ? "linear-gradient(180deg, rgba(15,20,27,0.45) 0%, rgba(15,20,27,0.85) 70%, #0f141b 100%)"
            : "linear-gradient(180deg, rgba(124,77,255,0.08) 0%, transparent 60%)"
        }} />
        <div className="relative flex flex-col items-center px-6 py-12 text-center">
          <div className="relative flex h-32 w-32 items-center justify-center">
            {glow && (
              <div
                aria-hidden
                className="comment-glow always-pulse absolute left-1/2 top-1/2 h-44 w-44 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${glow}cc 0%, ${glow}55 40%, ${glow}00 75%)`, transform: "translate(-50%, -50%)" }}
              />
            )}
            <img src={p.avatar_full} alt={p.habbo_name} className="hb-pixel-img relative z-10 h-32" />
          </div>
          <h1 className="pixel-text mt-3 text-3xl drop-shadow-lg" style={p.name_color ? { color: p.name_color } : { color: "white" }} data-testid="profile-habbo-name">
            {p.habbo_name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
            {p.verified && <span className="rounded-full bg-emerald-500/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-300 backdrop-blur-sm"><ShieldCheck className="mr-0.5 inline h-3 w-3" /> Verifiziert</span>}
            {p.tier && (
              <span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm" style={{ background: `${p.tier.color}33`, color: p.tier.color, boxShadow: `0 0 12px ${p.tier.color}55` }}>
                {p.tier.icon} {p.tier.label}
              </span>
            )}
            {p.team && (
              <span className="rounded-full bg-emerald-500/25 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-300 backdrop-blur-sm">TEAM · {p.team.position}</span>
            )}
          </div>
          <div className="mt-5 grid grid-cols-3 gap-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Punkte</div>
              <div className="pixel-text text-xl text-amber-300 drop-shadow">{p.points.toLocaleString("de-DE")}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Glows</div>
              <div className="pixel-text text-xl text-white drop-shadow">{p.unlocked_glows.length}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Projekte</div>
              <div className="pixel-text text-xl text-white drop-shadow">{p.projects.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Background-Picker (nur eigener Profil + wenn Hintergruende vorhanden) */}
      {isOwn && myBgs && myBgs.unlocked.length > 0 && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0f141b] p-5" data-testid="profile-background-picker">
          <h2 className="flex items-center gap-2 text-base font-semibold text-white"><ImageIcon className="h-4 w-4 text-[#b39bff]" /> Profil-Hintergrund</h2>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <button
              type="button"
              onClick={() => onActivateBg(null)}
              data-testid="profile-bg-none"
              className={`relative overflow-hidden rounded-lg border p-2 text-center text-xs transition-colors ${!myBgs.active ? "border-amber-400/60 bg-amber-500/10 text-amber-200" : "border-white/10 bg-[#10141b] text-zinc-300 hover:border-white/25"}`}
            >
              <div className="mb-1 h-14 w-full rounded bg-[#0a0d12]" />
              Kein Hintergrund
              {!myBgs.active && <Check className="absolute right-1 top-1 h-3 w-3 text-amber-300" />}
            </button>
            {myBgs.unlocked.map((b) => {
              const active = b.slug === myBgs.active;
              return (
                <button
                  type="button"
                  key={b.slug}
                  onClick={() => onActivateBg(b.slug)}
                  data-testid={`profile-bg-${b.slug}`}
                  className={`relative overflow-hidden rounded-lg border p-2 text-center text-xs transition-colors ${active ? "border-amber-400/60 bg-amber-500/10 text-amber-200" : "border-white/10 bg-[#10141b] text-zinc-300 hover:border-white/25"}`}
                >
                  <img src={b.image_url} alt={b.name} className="mb-1 h-14 w-full rounded object-cover" />
                  <div className="truncate">{b.name}</div>
                  {active && <Check className="absolute right-1 top-1 h-3 w-3 text-amber-300" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Projekte */}
      {p.projects.length > 0 && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-[#0f141b] p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold text-white"><Users className="h-4 w-4 text-[#b39bff]" /> Projekt-Teams</h2>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {p.projects.map((pr) => (
              <Link
                key={pr.slug}
                to={`/projekte/${pr.slug}`}
                data-testid={`profile-project-${pr.slug}`}
                className="flex items-center gap-3 rounded-lg border border-white/10 bg-[#10141b] p-3 hover:border-white/25"
              >
                {pr.badge_code && (
                  <div className="relative h-10 w-10 flex-shrink-0">
                    <div className="absolute inset-0 rounded-full blur-md opacity-60" style={{ backgroundColor: pr.badge_color }} />
                    <img src={`https://images.habbo.com/c_images/album1584/${pr.badge_code}.gif`} alt={pr.badge_code} className="hb-pixel-img relative h-10 w-10" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-white">{pr.title}</div>
                  {pr.role && <div className="text-[11px] text-zinc-400">{pr.role}</div>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Glows */}
      {p.unlocked_glows.length > 0 && (
        <div className="mt-6 rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold text-white"><Sparkles className="h-4 w-4 text-amber-300" /> Glow-Sammlung</h2>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {p.unlocked_glows.map((g) => {
              const isActive = g.slug === p.active_glow_slug;
              return (
                <div key={g.slug} className={`rounded-lg border p-3 ${isActive ? "border-amber-400/60 bg-amber-500/10" : "border-white/10 bg-[#10141b]"}`}>
                  <div
                    className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
                    style={g.gradient ? { background: g.gradient, boxShadow: `0 0 16px ${g.color}` } : { background: g.color, boxShadow: `0 0 16px ${g.color}` }}
                  >
                    <span className="text-sm">{g.icon}</span>
                  </div>
                  <div className="mt-2 text-center text-xs font-semibold text-white">{g.name}</div>
                  {isActive && <div className="mt-1 text-center text-[9px] uppercase tracking-wider text-amber-300">★ Aktiv</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {p.unlocked_glows.length === 0 && p.projects.length === 0 && (
        <div className="mt-6 rounded-xl border border-white/5 bg-[#10141b] p-6 text-center text-sm text-zinc-500">
          Dieser User ist noch frisch dabei – keine Glows oder Projekte bisher.
        </div>
      )}
    </section>
  );
};

export default UserProfile;
